import { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import '../src/css/globals.css';
import App from './App.tsx';
import Spinner from './views/spinner/Spinner.tsx';
import { ThemeProvider } from './context/shadcntheme/ThemeContext.tsx';
import { AuthProvider } from './context/auth/AuthContext.tsx';
import { initSentry } from './lib/sentry.ts';
import { installChunkErrorReload } from './lib/chunk-reload.ts';

// Before render, so a crash during the first paint is still reported. No-ops
// when no DSN is configured.
initSentry();

// Recovers a tab left open across a deploy — see lib/chunk-reload.ts.
installChunkErrorReload();

/**
 * Shown when a render-phase crash escapes everything below it. Deliberately
 * plain: it renders outside the providers, so it cannot assume the theme, the
 * session or the router are in a usable state.
 */
const CrashFallback = () => (
  <div className="min-h-dvh flex flex-col items-center justify-center gap-4 p-6 text-center">
    <p className="max-w-md text-muted-foreground">
      Something went wrong and this screen could not load. The error has been reported.
    </p>
    <button
      type="button"
      onClick={() => window.location.reload()}
      className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
    >
      Reload
    </button>
  </div>
);

createRoot(document.getElementById('root')!).render(
  <Sentry.ErrorBoundary fallback={<CrashFallback />}>
    <ThemeProvider defaultTheme="system" storageKey="panelgear-hq-theme">
      <AuthProvider>
        <Suspense fallback={<Spinner />}>
          <App />
        </Suspense>
      </AuthProvider>
    </ThemeProvider>
  </Sentry.ErrorBoundary>,
);
